// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { getSettingsCollection } from "@guestsupply/App/Store/Selectors/SettingsSelectors";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import changeQtyOrdered from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/ChangeQtyOrdered";
import changeUnitOfMeasure from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/ChangeUnitOfMeasure";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import ProductListProductCard from "@insite/content-library/Widgets/ProductList/ProductListProductCard";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Modal from "@insite/mobius/Modal";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
/* eslint-disable spire/fenced-imports */
import { hideAll } from "tippy.js";

interface OwnProps {
    products: ProductModel[];
}

const mapStateToProps = (state: ApplicationState) => ({
    isAuthenticated: getSession(state).isAuthenticated,
    isUsWebsite: getSettingsCollection(state).customSettings?.isUsWebSite,
    billTo: getCurrentBillToState(state)?.value,
    shipTo: getShipToState(state, getSession(state).shipToId)?.value,
    billToId: getSession(state).billToId,
    shipToId: getSession(state).shipToId,
    productsInfo: (state as CustomAppState).xUpsellsProducts.productsInfo,
});

const mapDispatchToProps = {
    changeUnitOfMeasure,
    changeQtyOrdered,
    updateProductInfo,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface UpsellsModalStyles {
    cardContainerStyles?: CardContainerStyles;
}

export const upsellsModalStyles: UpsellsModalStyles = {};

const styles = upsellsModalStyles;

const props = {
    showBrand: false,
    showTitle: true,
    showPartNumbers: true,
    showAvailability: true,
    showAttributes: false,
    showImage: true,
    showCompare: false,
    showPrice: true,
    showAddToList: false,
    showImageBadges: false,
    isUpSellsmodal: true,
};

const UpsellsModal = ({ products, productsInfo }: Props) => {
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (products.length) {
            setIsOpen(true);
            hideAll();
        }
    }, [products]);

    if (!products) {
        return null;
    }
    return (
        <Modal
            sizeVariant="large"
            headline={siteMessage("Guest_Cart_Upsells_Modal_Title", "Upsells products")}
            isOpen={isOpen}
            handleClose={() => setIsOpen(false)}
        >
            <LoadingOverlay loading={false}>
                {products.map((product, idx) => {
                    return (
                        <ProductCard
                            product={product}
                            key={idx}
                            idx={idx}
                            productInfo={productsInfo[product.productNumber]}
                        />
                    );
                })}
            </LoadingOverlay>
        </Modal>
    );
};

const ProductCard = ({
    product,
    productInfo,
    idx,
}: {
    product: ProductModel;
    productInfo: ProductInfo;
    idx: number;
}) => {
    const [qty, setQty] = useState(productInfo?.qtyOrdered);
    const [unitOfMeasure, setUnitOfMeasure] = useState(productInfo?.unitOfMeasure);

    if (!productInfo) {
        return null;
    }
    const updateQuantity = (qtyOrdered: number) => {
        setQty(qtyOrdered);
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        setUnitOfMeasure(unitOfMeasure);
    };
    const productContext: ProductContextModel = {
        product,
        productInfo: {
            ...productInfo,
            qtyOrdered: qty,
            unitOfMeasure,
        },
        idx,
        onQtyOrderedChanged: qtyOrdered => updateQuantity(qtyOrdered),
        onUnitOfMeasureChanged: unitOfMeasure => updateUnitOfMeasure(unitOfMeasure),
    };

    return (
        <ProductContext.Provider value={productContext} key={product.id}>
            <CardContainer extendedStyles={styles.cardContainerStyles}>
                <ProductListProductCard {...props} />
            </CardContainer>
        </ProductContext.Provider>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(UpsellsModal);
